/**
 * The in-call invite on the web, in every client call (platform and
 * interpreted rooms): ring someone in by @handle, email, number or saved
 * contact, and share a guest link.
 *
 * It opens from the toolbar's Invite (VrsInviteButton), the one invite UI per
 * room since the room pill (vrs-room-controls.js), which drew its own panel in
 * interpreted rooms, was retired on 2026-09-24. Every participant who is a
 * signed-in client may invite; the call holds 8 people.
 */
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { MAX_CALL_PARTICIPANTS, countCallPeople, sameParticipant } from '../../../../../shared/call-capacity';
import i18next from '../../../base/i18n/i18next';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import { copyText } from '../../../base/util/copyText.web';
import { getBrandShortName } from '../../../base/whitelabel/functions';
import { queueService } from '../../../interpreter-queue/InterpreterQueueService';
import { describeInviteEmailOutcome, isInviteEmailCandidate } from '../../../interpreter-queue/inviteEmailCopy';
import { showNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../notifications/constants';
import { apiClient } from '../../../shared/api-client';
import { getPersistentItem, getPersistentJson } from '../../../vrs-auth/storage';
import { INVITE_CANCEL_TIMEOUT_MS, INVITE_CONFIRM_TIMEOUT_MS, ROOM_INVITE_TOGGLE_EVENT, callCapacityText, canAskAgain, canCancel, contactInviteTarget, interpreterCanDial, inviteContactIdentity, inviteContactSecondaryIdentity, inviteContactsFromResponse, inviteResultText, inviteRowKey, inviteRowsReducer, inviteStatusText, privateCallNotice, privateCallText, typedInviteTarget } from '../../groupInvite';
import { inviteDialCountry } from '../../inviteDialCountry';
// The contacts API row mapper lives with the other invite rules now; it is
// re-exported for callers that import it from the panel.
export { toInviteContact } from '../../groupInvite';
let inviteSequence = 0;
function tr(key, options) {
    return String(i18next.t(key, options ?? {}));
}
/** The stored call, for the words a refusal needs (interpreted VRS or not). */
function inviteContext() {
    return { interpreterCanDial: interpreterCanDial(getPersistentJson('vrs_active_call')) };
}
/**
 * The short guest URL from a prepared invite: same-origin /j/<token> when the
 * server issued a token, the invite URL itself otherwise. Null when the
 * payload is not a live invite for this room or the URL is not http(s) —
 * the caller treats null as a mint failure.
 */
export function buildGuestLink(data, roomName) {
    if (!data || data.requestId === undefined || data.roomName !== roomName || data.status !== 'live') {
        return null;
    }
    try {
        const url = new URL(data.inviteUrl || '');
        if (url.protocol !== 'https:' && url.protocol !== 'http:') {
            return null;
        }
        return data.token ? `${url.origin}/j/${encodeURIComponent(data.token)}` : url.href;
    }
    catch (error) {
        return null;
    }
}
function rowForContact(rows, contact) {
    return rows.find(row => sameParticipant(inviteContactIdentity(contact), row.target));
}
const useStyles = makeStyles()(theme => ({
    // The call bar's glass (base/ui/constants.web.ts), a little more opaque
    // for reading.
    card: {
        position: 'fixed', right: theme.spacing(3), bottom: 152, zIndex: 380,
        background: 'rgba(8, 18, 32, 0.94)', color: theme.palette.text01,
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.16)', borderRadius: 12,
        boxShadow: '0 14px 34px rgba(0, 0, 0, 0.34)',
        padding: theme.spacing(3), width: 360, maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100dvh - 200px)', overflowY: 'auto', boxSizing: 'border-box',
        overflowWrap: 'anywhere'
    },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing(1) },
    // A colour of its own: a page-wide h2 rule painted it #333 on the dark card.
    title: { ...withPixelLineHeight(theme.typography.bodyShortBold), color: theme.palette.text01, margin: 0 },
    close: {
        background: 'none', border: 'none', color: theme.palette.text01, cursor: 'pointer',
        fontSize: '16px', lineHeight: 1, minHeight: 44, minWidth: 44,
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 }
    },
    capacity: {
        ...withPixelLineHeight(theme.typography.bodyShortRegular),
        color: theme.palette.text02, margin: `0 0 ${theme.spacing(2)}`
    },
    addRow: { display: 'flex', gap: theme.spacing(2), marginBottom: theme.spacing(2) },
    input: {
        background: 'transparent',
        border: `1px solid ${theme.palette.ui04}`,
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.text01,
        flex: 1,
        minHeight: 44,
        minWidth: 0,
        padding: `0 ${theme.spacing(2)}`,
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 1 }
    },
    status: {
        ...withPixelLineHeight(theme.typography.bodyShortRegular),
        color: theme.palette.text02, margin: `0 0 ${theme.spacing(2)}`
    },
    sectionTitle: {
        ...withPixelLineHeight(theme.typography.labelBold),
        color: theme.palette.text02, margin: `${theme.spacing(2)} 0 ${theme.spacing(1)}`
    },
    shareRow: { display: 'flex', gap: theme.spacing(2), marginBottom: theme.spacing(2) },
    row: {
        alignItems: 'center', display: 'flex', gap: theme.spacing(2),
        justifyContent: 'space-between', minHeight: 44, padding: `${theme.spacing(1)} 0`
    },
    rowText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
    rowName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    rowMeta: { ...withPixelLineHeight(theme.typography.bodyShortRegular), color: theme.palette.text02, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    rowStatus: { ...withPixelLineHeight(theme.typography.bodyShortRegular), color: theme.palette.text02 }
}));
/**
 * Rows for everyone this participant has asked in, kept while the panel is
 * closed so outcomes that arrive later still land: the queue events are
 * subscribed for as long as the room is up.
 */
function useRoomInvites(open) {
    const dispatch = useDispatch();
    const [rows, update] = useReducer(inviteRowsReducer, []);
    const openRef = useRef(open);
    const rowsRef = useRef(rows);
    const timers = useRef([]);
    openRef.current = open;
    rowsRef.current = rows;
    useEffect(() => {
        const current = (callId) => {
            const call = getPersistentJson('vrs_active_call');
            return Boolean(callId && call?.callId === callId);
        };
        const onSent = (data) => {
            if (current(data.callId)) {
                const resolved = data.target && typeof data.target === 'object'
                    ? data.target.name : undefined;
                update({ type: 'sent', inviteId: data.inviteId, name: typeof resolved === 'string' ? resolved : undefined,
                    requested: data.requested, ringing: data.ringing, target: data.target });
            }
        };
        const onResult = (data) => {
            if (!current(data.callId)) {
                return;
            }
            const row = rowsRef.current.find(item => item.inviteId && item.inviteId === data.inviteId);
            const name = row?.label || data.name || undefined;
            update({ type: 'result', inviteId: data.inviteId, name: data.name || undefined, status: data.status,
                targetId: data.targetId });
            // Accepted people announce themselves ("Dana joined"). A decline or
            // no answer would otherwise go unseen with the panel closed; a
            // decline shares its uid with the room's p2p_declined notice, so
            // the caller sees it once.
            if (name && !openRef.current && (data.status === 'declined' || data.status === 'missed')) {
                dispatch(showNotification({
                    titleKey: undefined,
                    title: inviteResultText(data.status, name, tr),
                    descriptionKey: undefined,
                    uid: data.status === 'declined' && data.targetId
                        ? `p2p-declined-${data.targetId}` : `room-invite-${data.inviteId || row?.key || name}`
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            }
        };
        const onError = (data) => {
            if (data?.operation === 'conference_add' && (!data.callId || current(String(data.callId)))) {
                update({ type: 'error', code: data.code });
            }
            if (data?.operation === 'conference_invite_cancel' && (!data.callId || current(String(data.callId)))) {
                update({ type: 'cancelFailed', code: data.code, targetId: data.targetId || undefined });
            }
        };
        queueService.on('conferenceInviteSent', onSent);
        queueService.on('conferenceInviteResult', onResult);
        queueService.on('error', onError);
        return () => {
            queueService.off('conferenceInviteSent', onSent);
            queueService.off('conferenceInviteResult', onResult);
            queueService.off('error', onError);
            timers.current.forEach(clearTimeout);
            timers.current = [];
        };
    }, [dispatch]);
    const invite = useCallback((target, label) => {
        const call = getPersistentJson('vrs_active_call');
        if (!call?.callId) {
            return;
        }
        const key = inviteRowKey(target);
        update({ type: 'sending', label, target });
        queueService.conferenceAdd(call.callId, target);
        timers.current.push(setTimeout(() => update({ type: 'unconfirmed', key }), INVITE_CONFIRM_TIMEOUT_MS));
    }, []);
    // Stop one ring. The row says "Cancelling…" until the server says how the
    // ring ended; offline, or no answer in time, it says the ring may go on.
    const cancel = useCallback(row => {
        const call = getPersistentJson('vrs_active_call');
        if (!call?.callId || !row.targetId || !canCancel(row)) {
            return;
        }
        update({ type: 'cancelling', key: row.key });
        if (!queueService.cancelConferenceInvite(call.callId, row.targetId)) {
            update({ type: 'cancelFailed', code: 'NOT_CONNECTED', key: row.key });
            return;
        }
        timers.current.push(setTimeout(() => update({ type: 'cancelFailed', code: 'TIMEOUT', key: row.key }), INVITE_CANCEL_TIMEOUT_MS));
    }, []);
    return { cancel, invite, rows };
}
/** The panel's contents; the shell below decides when it is shown. */
export function RoomInviteBody({ cancel, invite, isFull, participantCount, roomName, rows }) {
    const { classes } = useStyles();
    const [link, setLink] = useState(null);
    const [linkAttempt, setLinkAttempt] = useState(0);
    const [contactsAttempt, setContactsAttempt] = useState(0);
    const [contactsStatus, setContactsStatus] = useState('loading');
    const [contacts, setContacts] = useState([]);
    const [copied, setCopied] = useState(false);
    const [typed, setTyped] = useState('');
    const [fieldStatus, setFieldStatus] = useState('');
    const [emailBusy, setEmailBusy] = useState(false);
    const currentLink = link?.roomName === roomName ? link : null;
    const shortUrl = currentLink?.url || '';
    const privateCall = currentLink?.privateCall;
    useEffect(() => {
        setLink(null);
        if (isFull || !roomName) {
            return;
        }
        const requestId = `room-invite-${Date.now()}-${++inviteSequence}`;
        let pending = true;
        const fail = () => {
            if (pending) {
                pending = false;
                clearTimeout(timeout);
                setLink({ roomName, failed: true });
            }
        };
        const timeout = setTimeout(fail, 15000);
        const onPrepared = (data) => {
            if (!pending || data?.requestId !== requestId) {
                return;
            }
            const url = buildGuestLink(data, roomName);
            if (!url) {
                if (data.roomName === roomName) {
                    fail();
                }
                return;
            }
            pending = false;
            clearTimeout(timeout);
            setLink({ roomName, url });
        };
        const onError = (data) => {
            if (data?.operation === 'prepare_vri_invite' && data.requestId === requestId && data.roomName === roomName) {
                const notice = privateCallNotice(data);
                // The private demo pair's call: no guest link, and no retry.
                if (notice && pending) {
                    pending = false;
                    clearTimeout(timeout);
                    setLink({ roomName, privateCall: notice });
                    return;
                }
                fail();
            }
        };
        queueService.on('vriInvitePrepared', onPrepared);
        queueService.on('error', onError);
        try {
            queueService.prepareRoomInvite(roomName, requestId);
        }
        catch (error) {
            fail();
        }
        return () => {
            pending = false;
            clearTimeout(timeout);
            queueService.off('vriInvitePrepared', onPrepared);
            queueService.off('error', onError);
        };
    }, [roomName, isFull, linkAttempt]);
    useEffect(() => {
        let current = true;
        setContacts([]);
        setContactsStatus('loading');
        if (isFull || !roomName) {
            return;
        }
        const controller = new AbortController();
        const timeout = setTimeout(() => {
            current = false;
            setContactsStatus('failed');
            controller.abort();
        }, 15000);
        const load = async () => {
            try {
                const response = await apiClient.get('/api/contacts', { signal: controller.signal });
                const items = response.error ? null : inviteContactsFromResponse(response.data);
                if (!items) {
                    throw new Error('Contacts unavailable');
                }
                if (current) {
                    setContacts(items);
                    setContactsStatus('ready');
                }
            }
            catch (error) {
                if (current) {
                    setContactsStatus('failed');
                }
            }
            finally {
                clearTimeout(timeout);
            }
        };
        void load();
        return () => {
            current = false;
            clearTimeout(timeout);
            controller.abort();
        };
    }, [roomName, isFull, contactsAttempt]);
    const copyLink = useCallback(() => {
        if (!shortUrl) {
            return;
        }
        copyText(shortUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    }, [shortUrl]);
    const shareLink = useCallback(async () => {
        if (!shortUrl) {
            return;
        }
        if (typeof navigator.share === 'function') {
            const brand = getBrandShortName();
            try {
                await navigator.share({ title: tr('vrs.invite.panel.shareTitle', { brand }),
                    text: tr('vrs.invite.panel.shareText', { brand }), url: shortUrl });
                return;
            }
            catch (error) {
                // A dismissed share sheet is not an error; only a missing
                // sheet needs the fallback below.
                if (error instanceof Error && error.name === 'AbortError') {
                    return;
                }
            }
        }
        copyText(shortUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    }, [shortUrl]);
    const addTyped = useCallback(() => {
        const target = typedInviteTarget(typed, inviteDialCountry());
        if (isFull) {
            return;
        }
        if (!target) {
            setFieldStatus(tr('vrs.invite.field.invalid'));
            return;
        }
        invite(target, typed.trim());
        setTyped('');
        setFieldStatus('');
    }, [typed, invite, isFull]);
    // Someone without a Malka account can still join by guest link: offered
    // only after the email turned out not to be an account.
    const emailGuestLink = useCallback((email) => {
        if (emailBusy || !roomName || isFull || !isInviteEmailCandidate(email)) {
            return;
        }
        setEmailBusy(true);
        setFieldStatus('');
        const requestId = `room-email-${Date.now()}-${++inviteSequence}`;
        let pending = true;
        const finish = (outcome) => {
            if (!pending) {
                return;
            }
            pending = false;
            clearTimeout(timeout);
            queueService.off('vriInvitePrepared', onPrepared);
            queueService.off('error', onError);
            setEmailBusy(false);
            setFieldStatus(describeInviteEmailOutcome({ ...outcome, email }, tr));
        };
        const timeout = setTimeout(() => finish({ status: 'failed' }), 15000);
        const onPrepared = (data) => {
            if (!pending || data?.requestId !== requestId) {
                return;
            }
            const delivery = (data.deliveries || []).find(item => item.channel === 'email');
            finish(delivery
                ? { status: delivery.status, errorCode: delivery.errorCode || null }
                : { status: 'failed' });
        };
        const onError = (data) => {
            if (!pending || data?.operation !== 'prepare_vri_invite' || data.requestId !== requestId) {
                return;
            }
            finish({ status: 'failed', errorCode: data.code || null });
        };
        queueService.on('vriInvitePrepared', onPrepared);
        queueService.on('error', onError);
        queueService.prepareRoomInvite(roomName, requestId, { guestEmail: email, deliveryChannels: ['email'] });
    }, [emailBusy, roomName, isFull]);
    const renderAction = (row, onAsk, name) => {
        if (row && row.status === 'failed' && row.code === 'NOT_FOUND' && row.target.email && !privateCall) {
            return (React.createElement(Button, { accessibilityLabel: tr('vrs.invite.panel.emailLinkLabel', { name }), disabled: emailBusy || isFull, label: tr(emailBusy ? 'vrs.invite.status.sending' : 'vrs.invite.panel.emailLink'), onClick: () => emailGuestLink(row.target.email || ''), type: BUTTON_TYPES.SECONDARY }));
        }
        if (row && cancel && canCancel(row)) {
            return (React.createElement(Button, { accessibilityLabel: tr('vrs.invite.panel.cancelLabel', { name }), label: tr('vrs.invite.panel.cancel'), onClick: () => cancel(row), type: BUTTON_TYPES.SECONDARY }));
        }
        if (row && !canAskAgain(row)) {
            return null;
        }
        return (React.createElement(Button, { accessibilityLabel: tr('vrs.invite.panel.askLabel', { name }), disabled: isFull, label: tr(row ? 'vrs.invite.panel.askAgain' : 'vrs.invite.panel.ask'), onClick: onAsk, type: BUTTON_TYPES.SECONDARY }));
    };
    // A person appears once: a saved contact shows their invite status in
    // their own row, and only people who are not contacts get a row here.
    const otherRows = rows.filter(row => !contacts.some(contact => sameParticipant(inviteContactIdentity(contact), row.target)));
    const context = inviteContext();
    return (React.createElement(React.Fragment, null,
        React.createElement("p", { "aria-live": 'polite', className: classes.capacity }, callCapacityText(isFull ? MAX_CALL_PARTICIPANTS : participantCount, tr)),
        privateCall && (React.createElement("p", { "aria-live": 'polite', className: classes.status, role: 'status' }, privateCallText(privateCall, tr))),
        !isFull && (!privateCall || privateCall.demoAccountsOnly) && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classes.addRow },
                React.createElement("input", { "aria-label": tr('vrs.invite.panel.fieldLabel'), autoCapitalize: 'off', autoComplete: 'off', autoCorrect: 'off', className: classes.input, "data-1p-ignore": 'true', "data-lpignore": 'true', "data-room-invite-field": 'true', name: 'malka-room-invite-target', onChange: event => {
                        setTyped(event.target.value);
                        setFieldStatus('');
                    }, onKeyDown: event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            addTyped();
                        }
                    }, placeholder: tr('vrs.invite.panel.fieldPlaceholder'), spellCheck: false, type: 'text', value: typed }),
                React.createElement(Button, { accessibilityLabel: tr('vrs.invite.panel.addLabel'), label: tr('vrs.invite.panel.add'), onClick: addTyped, type: BUTTON_TYPES.PRIMARY })),
            fieldStatus && React.createElement("p", { "aria-live": 'polite', className: classes.status, role: 'status' }, fieldStatus),
            otherRows.map(row => (React.createElement("div", { className: classes.row, "data-invite-row": row.key, key: row.key },
                React.createElement("span", { className: classes.rowText },
                    React.createElement("span", { className: classes.rowName }, row.label),
                    React.createElement("span", { "aria-live": 'polite', className: classes.rowStatus }, inviteStatusText(row, tr, context))),
                renderAction(row, () => invite(row.target, row.label), row.label)))),
            contacts.length > 0 && React.createElement("h3", { className: classes.sectionTitle }, tr('vrs.invite.panel.contacts')),
            contactsStatus !== 'ready' && (React.createElement("p", { "aria-live": 'polite', className: classes.status }, tr(contactsStatus === 'failed' ? 'vrs.invite.panel.contactsFailed' : 'vrs.invite.panel.contactsLoading'))),
            contactsStatus === 'failed' && (React.createElement(Button, { accessibilityLabel: tr('vrs.invite.panel.contactsRetryLabel'), fullWidth: true, label: tr('vrs.invite.panel.contactsRetry'), onClick: () => setContactsAttempt(value => value + 1), type: BUTTON_TYPES.SECONDARY })),
            contacts.map(contact => {
                const row = rowForContact(rows, contact);
                const name = contact.name || contact.handle || contact.phoneNumber
                    || tr('vrs.invite.panel.contactFallback');
                const secondary = inviteContactSecondaryIdentity(contact);
                const target = contactInviteTarget(contact);
                return (React.createElement("div", { className: classes.row, key: contact.id },
                    React.createElement("span", { className: classes.rowText },
                        React.createElement("span", { className: classes.rowName }, name),
                        secondary && secondary !== name && (React.createElement("span", { className: classes.rowMeta }, secondary)),
                        row && (React.createElement("span", { "aria-live": 'polite', className: classes.rowStatus }, inviteStatusText(row, tr, context)))),
                    target && renderAction(row, () => invite(target, name), name)));
            }),
            !privateCall && (React.createElement(React.Fragment, null,
                React.createElement("h3", { className: classes.sectionTitle }, tr('vrs.invite.panel.guestLink')),
                React.createElement("div", { className: classes.shareRow },
                    React.createElement(Button, { accessibilityLabel: tr('vrs.invite.panel.copyLinkLabel'), disabled: !shortUrl, fullWidth: true, label: tr(copied ? 'vrs.invite.panel.copied' : 'vrs.invite.panel.copyLink'), onClick: copyLink, type: BUTTON_TYPES.SECONDARY }),
                    React.createElement(Button, { accessibilityLabel: tr('vrs.invite.panel.shareLabel'), disabled: !shortUrl, fullWidth: true, label: tr('vrs.invite.panel.share'), onClick: shareLink, type: BUTTON_TYPES.SECONDARY })),
                React.createElement("p", { "aria-live": 'polite', className: classes.status }, shortUrl
                    ? `${shortUrl.replace(/^https?:\/\//, '')}`
                    : tr(currentLink?.failed ? 'vrs.invite.panel.linkFailed'
                        : roomName ? 'vrs.invite.panel.linkCreating' : 'vrs.invite.panel.linkUnavailable')),
                currentLink?.failed && (React.createElement(Button, { accessibilityLabel: tr('vrs.invite.panel.linkRetryLabel'), fullWidth: true, label: tr('vrs.invite.panel.linkRetry'), onClick: () => setLinkAttempt(value => value + 1), type: BUTTON_TYPES.SECONDARY }))))))));
}
/**
 * The panel shell, in every client call (platform and interpreted rooms),
 * opened by the toolbar's Invite. Signed-in clients only: guests on a guest
 * link and interpreters never see it.
 */
export default function InvitePanelWeb() {
    const { classes } = useStyles();
    const [open, setOpen] = useState(false);
    // A team interpreter is not a counted person (countCallPeople).
    const participantCount = useSelector((state) => countCallPeople(state['features/base/participants'].remote.values()));
    const roomName = useSelector((state) => state['features/base/conference'].room) || '';
    const isClient = getPersistentItem('vrs_user_role') === 'client';
    const { cancel, invite, rows } = useRoomInvites(open);
    const isFull = participantCount >= MAX_CALL_PARTICIPANTS;
    const panel = useRef(null);
    useEffect(() => {
        if (!isClient) {
            return;
        }
        const toggle = () => setOpen(value => !value);
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        window.addEventListener(ROOM_INVITE_TOGGLE_EVENT, toggle);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener(ROOM_INVITE_TOGGLE_EVENT, toggle);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isClient]);
    useEffect(() => {
        if (open) {
            panel.current?.querySelector('[data-room-invite-field]')?.focus();
        }
    }, [open]);
    const body = useMemo(() => ({ cancel, invite, isFull, participantCount, roomName, rows }), [cancel, invite, isFull, participantCount, roomName, rows]);
    if (!isClient || !open) {
        return null;
    }
    return (React.createElement("div", { "aria-label": tr('vrs.invite.panel.title'), className: classes.card, "data-room-invite-panel": 'true', ref: panel, role: 'dialog' },
        React.createElement("div", { className: classes.header },
            React.createElement("h2", { className: classes.title }, tr('vrs.invite.panel.title')),
            React.createElement("button", { "aria-label": tr('vrs.invite.panel.close'), className: classes.close, onClick: () => setOpen(false), type: 'button' }, "\u2715")),
        React.createElement(RoomInviteBody, { ...body })));
}
